import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { AlertTriangle, Clock, X } from "lucide-react";
import { Link } from "react-router-dom";

export default function TrialStatusBanner() {
  const { t } = useLanguage();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/billing/subscription")
      .then((r) => setSub(r.data))
      .catch(() => setSub(null));
  }, []);

  if (!sub || sub.plan !== "free_trial" || dismissed) return null;

  const readOnly = sub.trial_read_only;
  const days = sub.trial_days_remaining;
  const pendingDelete = !!sub.pending_delete_at;

  // READ-ONLY STATE (trial expired)
  if (readOnly) {
    return (
      <div
        className="w-full bg-gradient-to-r from-red-500/15 via-rose-500/15 to-red-500/15 border-b border-red-500/30 px-4 py-2.5 flex items-center justify-between gap-3"
        data-testid="trial-readonly-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-xs sm:text-sm font-semibold text-red-300 truncate">
            {pendingDelete
              ? t("Workspace scheduled for deletion — upgrade to restore access.")
              : t("Your free trial has ended. Workspace is now read-only.")}
          </span>
        </div>
        <Link
          to="/settings?tab=billing"
          className="text-[11px] font-bold px-3 py-1 rounded-lg bg-red-500 hover:bg-red-400 text-white transition-all shrink-0"
          data-testid="trial-upgrade-cta"
        >
          {t("Upgrade Now")}
        </Link>
      </div>
    );
  }

  // FINAL 4 DAYS — scarcity banner
  if (days !== null && days <= 4) {
    const tone = days <= 1 ? "red" : days <= 2 ? "amber" : "cyan";
    const toneMap = {
      red: "from-red-500/15 to-rose-500/15 border-red-500/30 text-red-300",
      amber: "from-amber-500/15 to-orange-500/15 border-amber-500/30 text-amber-300",
      cyan: "from-cyan-500/10 to-blue-500/10 border-cyan-500/20 text-cyan-300",
    };
    return (
      <div
        className={`w-full bg-gradient-to-r ${toneMap[tone]} border-b px-4 py-2 flex items-center justify-between gap-3`}
        data-testid="trial-scarcity-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-3.5 h-3.5 shrink-0" />
          <span className="text-[11px] sm:text-xs font-semibold truncate">
            {days === 0
              ? t("Your trial expires today — upgrade to keep your workspace active.")
              : days === 1
              ? t("1 day left on your free trial.")
              : t("{n} days left on your free trial.").replace("{n}", days)}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/settings?tab=billing"
            className="text-[10px] sm:text-[11px] font-bold px-2.5 py-1 rounded-md bg-[var(--app-text)]/10 hover:bg-[var(--app-text)]/20 transition-all"
            data-testid="trial-upgrade-link"
          >
            {t("Upgrade")}
          </Link>
          <button
            onClick={() => setDismissed(true)}
            className="opacity-60 hover:opacity-100 transition"
            aria-label="Dismiss"
            data-testid="trial-banner-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
